// Optional props default via JS default parameters below. defaultProps is
// deprecated for function components in React 18+, so we opt out of the
// require-default-props rule here rather than reintroduce that warning.
/* eslint-disable react/require-default-props */
import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useRecommendedRates } from '../../../domain/Jobs';

/**
 * Headless bridge that lets class components consume the useRecommendedRates
 * hook. Fetches recommended rates for `officeId`, reports the rate info
 * (recommended average + min/max range + loading) for the selected profession
 * via `onInfoChange`, and — whenever a recommendation is available (and
 * `enabled`) — calls `onResolved` with it to seed the default. Renders nothing.
 */
export const RecommendedRateWatcher = ({
  officeId = undefined,
  profession = undefined,
  subProfession = null,
  enabled = true,
  onResolved,
  onInfoChange = () => {},
}) => {
  const { getRateInfo, isLoading } = useRecommendedRates(officeId);

  const entry = useMemo(
    () => getRateInfo({ profession, subProfession }),
    [getRateInfo, profession, subProfession],
  );

  const recommended = entry ? Math.round(entry.average) : null;
  const min = entry?.min ?? null;
  const max = entry?.max ?? null;

  useEffect(() => {
    onInfoChange({ recommended, min, max, isLoading });
  }, [recommended, min, max, isLoading, onInfoChange]);

  useEffect(() => {
    if (!officeId || !enabled) {
      return;
    }

    if (recommended != null) {
      onResolved(recommended);
    }
  }, [officeId, enabled, recommended, onResolved]);

  return null;
};

RecommendedRateWatcher.propTypes = {
  officeId: PropTypes.string,
  profession: PropTypes.string,
  subProfession: PropTypes.string,
  enabled: PropTypes.bool,
  onResolved: PropTypes.func.isRequired,
  onInfoChange: PropTypes.func,
};
